/* eslint-disable no-console */
import { useCallback, useMemo } from 'react';
import {
  useApolloCacheUtilities,
  getFragmentName,
} from '../../util/apolloUtils';
import {
  Project,
  ProjectEdge,
} from '../../../server/graphql/__generated.types';
import { ProjectFragment, ProjectFragmentDoc } from '../fragments.generated';
import {
  PersonalProjectsDocument,
  PersonalProjectsQuery,
  PersonalProjectsQueryVariables,
  SharedProjectsDocument,
  SharedProjectsQuery,
  SharedProjectsQueryVariables,
  TeamProjects_V2Document,
  TeamProjects_V2Query,
  TeamProjects_V2QueryVariables,
  UserProjectDocument,
  UserProjectQuery,
  UserProjectQueryVariables,
} from './operation.generated';
import useProjectsOrderBy from './Listing/NewProjectsList/useProjectsOrderBy';

const logger = (enable: boolean): Pick<Console, 'info' | 'error'> => {
  return enable
    ? { info: console.info, error: console.error }
    : { info: () => {}, error: () => {} };
};

const log = logger(false);

const fragmentName = getFragmentName(ProjectFragmentDoc);

export const convertProjectFragmentsToProjectEdges = (
  fragments: Array<ProjectFragment>
): Array<ProjectEdge> => {
  return fragments.map(p => ({
    __typename: 'ProjectEdge',
    node: p as Project,
    cursor: '',
  }));
};

const useProjectCacheUtilities = () => {
  const { client, identityInCache } = useApolloCacheUtilities();

  const { orderBy } = useProjectsOrderBy();

  /**
   * Helper method to read from personalProjects cache
   */
  const readPersonalProjectsCache = useCallback(() => {
    const personalProjectsCached = client.readQuery<
      PersonalProjectsQuery,
      PersonalProjectsQueryVariables
    >({
      query: PersonalProjectsDocument,
      variables: {
        inputs: {
          orderBy,
        },
      },
    });
    return personalProjectsCached;
  }, [client, orderBy]);

  /**
   * Helper method to write edges to personalProjects cache
   */
  const writeEdgesToPersonalProjectsCache = useCallback(
    (cached: PersonalProjectsQuery, edges: ProjectEdge[]) => {
      client.writeQuery<PersonalProjectsQuery, PersonalProjectsQueryVariables>({
        query: PersonalProjectsDocument,
        variables: {
          inputs: {
            orderBy,
          },
        },
        data: {
          ...cached,
          user: {
            ...cached.user,
            personalProjects: {
              __typename: 'ProjectsConnection',
              ...cached.user.personalProjects,
              edges,
            },
          },
        },
      });
    },
    [client, orderBy]
  );

  /**
   * Helper method to remove a certain project from the personal projects edges array
   */
  const removeProjectFromPersonalProjectsEdges = useCallback(
    (cached: PersonalProjectsQuery, projectId: string) => {
      const personalProjects: Array<ProjectFragment> =
        cached.user.personalProjects?.edges.map(edge => edge.node) ?? [];

      const pIndex = personalProjects.findIndex(p => p.id === projectId);
      const removedProject = personalProjects.splice(pIndex, 1);

      return {
        removedProject: removedProject[0],
        edges: convertProjectFragmentsToProjectEdges(personalProjects),
      };
    },
    []
  );

  /**
   * Helper method to add a project fragment to the personal projects edges array
   */
  const addProjectToPersonalProjectsEdges = useCallback(
    (cached: PersonalProjectsQuery, project: ProjectFragment) => {
      // add to personal projects query
      const personalProjects: Array<ProjectFragment> =
        cached.user.personalProjects?.edges.map(edge => edge.node) ?? [];
      personalProjects.unshift(project);
      return convertProjectFragmentsToProjectEdges(personalProjects);
    },
    []
  );

  /**
   * Helper method to read from teamProjects cache
   */
  const readTeamProjectsCache = useCallback(
    (options?: { folderId?: string; driveId?: string }) => {
      const filters =
        options && (options?.folderId || options?.driveId)
          ? {
              folderId: options?.folderId,
              driveId: options?.driveId,
            }
          : undefined;
      const teamProjectsCached = client.readQuery<
        TeamProjects_V2Query,
        TeamProjects_V2QueryVariables
      >({
        query: TeamProjects_V2Document,
        variables: {
          inputs: {
            orderBy,
            filters,
          },
        },
      });
      return teamProjectsCached;
    },
    [client, orderBy]
  );

  /**
   * Helper method to write edges to teamProjects cache
   */
  const writeEdgesToTeamProjectsCache = useCallback(
    (cached: TeamProjects_V2Query, edges: ProjectEdge[]) => {
      client.writeQuery<TeamProjects_V2Query, TeamProjects_V2QueryVariables>({
        query: TeamProjects_V2Document,
        variables: {
          inputs: {
            orderBy,
          },
        },
        data: {
          ...cached,
          user: {
            ...cached.user,
            teamProjects: {
              __typename: 'ProjectsConnection',
              ...cached.user.teamProjects,
              edges,
            },
          },
        },
      });
    },
    [client, orderBy]
  );

  /**
   * Helper method to remove a certain project from the team projects edges array
   */
  const removeProjectFromTeamProjectsEdges = useCallback(
    (cached: TeamProjects_V2Query, projectId: string) => {
      const teamProjects: Array<ProjectFragment> =
        cached.user.teamProjects?.edges.map(edge => edge.node) ?? [];
      const pIndex = teamProjects.findIndex(p => p.id === projectId);
      const removedProject = teamProjects.splice(pIndex, 1);

      return {
        removedProject: removedProject[0],
        edges: convertProjectFragmentsToProjectEdges(teamProjects),
      };
    },
    []
  );

  /**
   * Helper method to add a project fragment to the team projects edges array
   */
  const addProjectToTeamProjectsEdges = useCallback(
    (cached: TeamProjects_V2Query, project: ProjectFragment) => {
      // add to team projects query
      const teamProjects: Array<ProjectFragment> =
        cached.user.teamProjects?.edges.map(edge => edge.node) ?? [];
      teamProjects.unshift(project);
      return convertProjectFragmentsToProjectEdges(teamProjects);
    },
    []
  );

  /**
   * Helper method to read from sharedProjects cache
   */
  const readSharedProjectsCache = useCallback(() => {
    const sharedProjectsCached = client.readQuery<
      SharedProjectsQuery,
      SharedProjectsQueryVariables
    >({
      query: SharedProjectsDocument,
      variables: {
        inputs: {
          orderBy,
        },
      },
    });
    return sharedProjectsCached;
  }, [client, orderBy]);

  /**
   * Helper method to write edges to sharedProjects cache
   */
  const writeEdgesToSharedProjectsCache = useCallback(
    (cached: SharedProjectsQuery, edges: ProjectEdge[]) => {
      client.writeQuery<SharedProjectsQuery, SharedProjectsQueryVariables>({
        query: SharedProjectsDocument,
        variables: {
          inputs: {
            orderBy,
          },
        },
        data: {
          ...cached,
          user: {
            ...cached.user,
            sharedProjects: {
              __typename: 'ProjectsConnection',
              ...cached.user.sharedProjects,
              edges,
            },
          },
        },
      });
    },
    [client, orderBy]
  );

  /**
   * Helper method to remove a certain project from the shared projects edges array
   */
  const removeProjectFromSharedProjectsEdges = useCallback(
    (cached: SharedProjectsQuery, projectId: string) => {
      const sharedProjects: Array<ProjectFragment> =
        cached.user.sharedProjects?.edges.map(edge => edge.node) ?? [];

      const pIndex = sharedProjects.findIndex(p => p.id === projectId);
      const removedProject = sharedProjects.splice(pIndex, 1);

      return {
        removedProject: removedProject[0],
        edges: convertProjectFragmentsToProjectEdges(sharedProjects),
      };
    },
    []
  );

  /**
   * Helper method to add a project fragment to the shared projects edges array
   */
  const addProjectToSharedProjectsEdges = useCallback(
    (cached: SharedProjectsQuery, project: ProjectFragment) => {
      // add to shared projects query
      const sharedProjects: Array<ProjectFragment> =
        cached.user.sharedProjects?.edges.map(edge => edge.node) ?? [];
      sharedProjects.unshift(project);
      return convertProjectFragmentsToProjectEdges(sharedProjects);
    },
    []
  );

  const writeProjectFragment = useCallback(
    (project: ProjectFragment) => {
      const id = identityInCache('Project', project.id);
      log.info(`writeProjectFragment: ${id}`);
      return client.writeFragment<ProjectFragment>({
        id,
        fragment: ProjectFragmentDoc,
        fragmentName,
        data: project,
      });
    },
    [client, identityInCache]
  );

  const writeUserProjectQuery = useCallback(
    (userId: string, projectEdges: ProjectEdge[]) => {
      projectEdges.forEach(edge => {
        try {
          client.writeQuery<UserProjectQuery, UserProjectQueryVariables>({
            query: UserProjectDocument,
            variables: {
              inputs: {
                projectId: edge.node.id,
              },
            },
            data: {
              user: {
                __typename: 'User',
                id: userId,
                project: {
                  __typename: 'Project',
                  ...edge.node,
                  voices: edge.node.voices ?? [],
                },
              },
            },
          });
        } catch (e) {
          console.error(`Error writing to cache:`, e);
        }
      });
    },
    [client]
  );

  const readProjectFragment = useCallback(
    (id: string) => {
      log.info(`readProjectFragment: ${id}`);
      return client.readFragment<ProjectFragment>({
        id: identityInCache('Project', id),
        fragment: ProjectFragmentDoc,
        fragmentName,
      });
    },
    [client, identityInCache]
  );

  const projectInCacheById = useCallback(
    (id: string) => {
      log.info(`projectInCacheById: ${id}`);
      const project = readProjectFragment(id);
      return project || undefined;
    },
    [readProjectFragment]
  );

  const updateProjectInCache = useCallback(
    (project: ProjectFragment) => {
      log.info('updateProjectInCache');
      const projectToUpdate = projectInCacheById(project.id);
      if (!projectToUpdate) {
        log.error(`updateProjectInCache: project ${project.id} does not exist`);
        return;
      }
      writeProjectFragment({
        ...projectToUpdate,
        ...project,
      });
    },
    [projectInCacheById, writeProjectFragment]
  );

  const removeProjectFromCache = useCallback(
    (project: ProjectFragment) => {
      log.info(`removeProjectFromCache: projectId ${project.id}`);

      const exists = projectInCacheById(project.id);
      if (!exists) {
        log.error(
          `removeProjectFromCache: projectId: ${project.id} does not exist`
        );
        return;
      }
      client.cache.evict({ id: identityInCache('Project', project.id) });
      client.cache.gc();
    },
    [client.cache, identityInCache, projectInCacheById]
  );

  return useMemo(() => {
    return {
      readPersonalProjectsCache,
      writeEdgesToPersonalProjectsCache,
      removeProjectFromPersonalProjectsEdges,
      addProjectToPersonalProjectsEdges,
      readTeamProjectsCache,
      writeEdgesToTeamProjectsCache,
      removeProjectFromTeamProjectsEdges,
      addProjectToTeamProjectsEdges,
      readSharedProjectsCache,
      writeEdgesToSharedProjectsCache,
      removeProjectFromSharedProjectsEdges,
      addProjectToSharedProjectsEdges,
      updateProjectInCache,
      readProjectFragment,
      removeProjectFromCache,
      writeUserProjectQuery,
    };
  }, [
    readPersonalProjectsCache,
    writeEdgesToPersonalProjectsCache,
    removeProjectFromPersonalProjectsEdges,
    addProjectToPersonalProjectsEdges,
    readTeamProjectsCache,
    writeEdgesToTeamProjectsCache,
    removeProjectFromTeamProjectsEdges,
    addProjectToTeamProjectsEdges,
    readSharedProjectsCache,
    writeEdgesToSharedProjectsCache,
    removeProjectFromSharedProjectsEdges,
    addProjectToSharedProjectsEdges,
    updateProjectInCache,
    readProjectFragment,
    removeProjectFromCache,
    writeUserProjectQuery,
  ]);
};

export default useProjectCacheUtilities;
